// @ts-nocheck
/**
 * chat-render.js — All rendering logic for the AI Florist chat.
 *
 * Handles: message bubbles, image cards, price cards, checkout buttons,
 * system errors, typing/designing indicators, and utility formatters.
 *
 * Dependencies: chat-fetch.js (for image URLs only)
 *
 * Exposes:
 *   window.AIFlorist.render.init(deps)
 *   window.AIFlorist.render.message(role, content, metadata)
 *   window.AIFlorist.render.systemError(message)
 *   window.AIFlorist.render.setLoading(loading)
 *   window.AIFlorist.render.clearMessages()
 *   window.AIFlorist.render.escapeHtml(str)
 *   window.AIFlorist.render.formatDate(isoStr)
 *   window.AIFlorist.render.getBadgeClass(state)
 *   window.AIFlorist.render.getBadgeLabel(state)
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};

  // ── Dependencies (set by init) ─────────────────────────────────────
  var messagesEl = null;
  var inputEl = null;
  var sendBtn = null;
  var workerUrl = '';
  var analytics = null;

  // ── Loading state ──────────────────────────────────────────────────
  var designingTimerId = null;
  var lastFailedMessage = null;
  var retryCount = 0;
  var MAX_RETRIES = 2;
  var onRetry = null; // callback(text)
  var imagePreviewEl = null;
  var imagePreviewImgEl = null;
  var addToOrderEnabled = false;
  var hasActiveCartItems = false;

  /** Callbacks for price card buttons */
  var onProceed = null;   // (selectionRefWithCardMessage) => Promise<{success:boolean}>
  var onRegenerate = null; // () => void — "Regenerate" clicked
  var onRevise = null; // (opts) => void — checkout revision selected
  var onDecisionProceed = null; // () => Promise<{success:boolean}> — pre-generation CTA proceed
  var onDecisionRevise = null; // () => Promise<{success:boolean}> — pre-generation CTA revise
  var onOpenWeddingCreative = null; // (prefill) => void — open wedding creative brief
  var onOpenWeddingDetails = null; // (prefill) => void — open wedding details form
  var onWeddingConceptRegenerate = null; // () => void — enter wedding concept tweak flow
  var onWelcomeReferenceCta = null; // () => void — welcome reference upload CTA clicked
  var onWelcomeReferenceClear = null; // () => void — clear welcome reference upload
  var onWelcomeReferenceRemove = null; // (index: number) => void — remove image at index from welcome thumbs
  var uiLanguage = 'en';
  var DICT = {
    en: {
      preparingOrder: 'Preparing order…',
      addingToOrder: 'Adding your bouquet…',
      finalizingAddToOrder: 'Getting it ready…',
      retryingAddToOrder: 'Still syncing…',
      addedToOrder: 'Added - view cart below',
      imageAttached: '(image attached)',
      imageLoadError: 'Unable to load image',
      proceedToCheckout: 'Proceed to Checkout',
      goToCart: 'View cart',
      revise: 'Revise',
      regenerateProduct: 'Regenerate Product',
      reviseOrderDetails: 'Revise Order Details',
      applyRevision: 'Apply Revision',
      checkoutNote: 'Secure checkout via Shopify. Delivery goes to the recipient; billing stays with you.',
      arrangementFallbackName: 'Your Custom Arrangement',
      arrangementFallbackDescription: 'Handcrafted just for you',
      regenerate: 'Regenerate',
      proceedToOrder: 'Proceed to Order',
      proceedWithDesign: 'Proceed with this design',
      makeChanges: 'Make changes',
      addToOrder: 'Add this bouquet',
      cardMessageLabel: 'Gift card message',
      cardMessagePlaceholder: 'Optional - To Mum, with love...',
      selectVariation: 'Select',
      editThisDesign: 'Edit this design',
      selectBouquetHint: 'Pick the bouquet you like first, then I can add that exact one.',
      cartNextStepHint: 'Once it is added, head to cart to choose delivery details and checkout.',
      tryAgain: 'Try Again',
      designingTitle: 'Designing your arrangement',
      designingSubtitle: 'This usually takes 30–45 seconds',
      today: 'Today',
      yesterday: 'Yesterday',
      completed: 'Completed',
      reviewing: 'Reviewing',
      creating: 'Creating',
      inProgress: 'In progress',
      chat: 'Chat',
      welcomeReferenceHint: 'Attach an image to help Fleur understand your vision',
      whatsInside: "What's inside",
    },
    zh: {
      preparingOrder: '正在准备订单…',
      addingToOrder: 'Adding…',
      finalizingAddToOrder: 'Finalizing item…',
      retryingAddToOrder: 'Syncing with Shopify…',
      addedToOrder: 'Added',
      imageAttached: '（已附加图片）',
      imageLoadError: '无法加载图片',
      proceedToCheckout: '前往结账',
      goToCart: 'Go to Cart',
      revise: '修改',
      regenerateProduct: '重新生成商品',
      reviseOrderDetails: '修改订单信息',
      applyRevision: '应用修改',
      checkoutNote: '通过 Shopify 安全结账 · 收货地址=收件人 · 账单信息=下单人',
      arrangementFallbackName: '您的专属花艺作品',
      arrangementFallbackDescription: '专为您手工设计',
      regenerate: '重新生成',
      proceedToOrder: '继续下单',
      proceedWithDesign: '就按这个设计生成',
      makeChanges: '继续修改',
      addToOrder: 'Add to Order',
      cardMessageLabel: 'Card message (for this arrangement)',
      cardMessagePlaceholder: 'Optional: To... From... with love',
      selectVariation: '选择',
      editThisDesign: '修改此设计',
      selectBouquetHint: 'Please select a bouquet before adding to order.',
      cartNextStepHint: 'Once it is added, head to cart to choose delivery details and checkout.',
      tryAgain: '重试',
      designingTitle: '正在设计您的花艺',
      designingSubtitle: '通常需要 30–45 秒',
      today: '今天',
      yesterday: '昨天',
      completed: '已完成',
      reviewing: '修改中',
      creating: '创作中',
      inProgress: '进行中',
      chat: '会话',
      welcomeReferenceHint: '上传图片帮助 Fleur 更好地理解您的想法',
      whatsInside: '花材明细',
    },
    ja: {
      preparingOrder: '注文を準備しています…',
      addingToOrder: 'Adding…',
      finalizingAddToOrder: 'Finalizing item…',
      retryingAddToOrder: 'Syncing with Shopify…',
      addedToOrder: 'Added',
      imageAttached: '（画像が添付されています）',
      imageLoadError: '画像を読み込めません',
      proceedToCheckout: 'チェックアウトへ進む',
      goToCart: 'Go to Cart',
      revise: '修正',
      regenerateProduct: '商品を再生成',
      reviseOrderDetails: '注文情報を修正',
      applyRevision: '修正を適用',
      checkoutNote: 'Shopify の安全な決済 · 配送先=受取人住所 · 請求先=注文者情報',
      arrangementFallbackName: 'あなただけのアレンジメント',
      arrangementFallbackDescription: 'あなたのために丁寧に制作',
      regenerate: '再生成',
      proceedToOrder: '注文へ進む',
      proceedWithDesign: 'このデザインで進める',
      makeChanges: '変更する',
      addToOrder: 'Add to Order',
      cardMessageLabel: 'Card message (for this arrangement)',
      cardMessagePlaceholder: 'Optional: To... From... with love',
      selectVariation: '選択',
      editThisDesign: 'このデザインを修正',
      selectBouquetHint: 'Please select a bouquet before adding to order.',
      cartNextStepHint: 'Once it is added, head to cart to choose delivery details and checkout.',
      tryAgain: '再試行',
      designingTitle: 'アレンジメントをデザイン中',
      designingSubtitle: '通常 30〜45 秒ほどかかります',
      today: '今日',
      yesterday: '昨日',
      completed: '完了',
      reviewing: '確認中',
      creating: '作成中',
      inProgress: '進行中',
      chat: 'チャット',
      welcomeReferenceHint: '画像を添付して Fleur にあなたのイメージを伝えましょう',
      whatsInside: '花材一覧',
    },
    ko: {
      preparingOrder: '주문을 준비하는 중…',
      addingToOrder: 'Adding…',
      finalizingAddToOrder: 'Finalizing item…',
      retryingAddToOrder: 'Syncing with Shopify…',
      addedToOrder: 'Added',
      imageAttached: '(이미지 첨부됨)',
      imageLoadError: '이미지를 불러올 수 없습니다',
      proceedToCheckout: '결제로 이동',
      goToCart: 'Go to Cart',
      revise: '수정',
      regenerateProduct: '상품 다시 생성',
      reviseOrderDetails: '주문 정보 수정',
      applyRevision: '수정 적용',
      checkoutNote: 'Shopify 안전 결제 · 배송지=수령인 주소 · 청구지=주문자 정보',
      arrangementFallbackName: '고객님만의 플라워 어레인지',
      arrangementFallbackDescription: '고객님을 위해 수작업으로 제작',
      regenerate: '다시 생성',
      proceedToOrder: '주문 진행',
      proceedWithDesign: '이 디자인으로 진행',
      makeChanges: '수정 계속하기',
      addToOrder: 'Add to Order',
      cardMessageLabel: 'Card message (for this arrangement)',
      cardMessagePlaceholder: 'Optional: To... From... with love',
      selectVariation: '선택',
      editThisDesign: '이 디자인 수정',
      selectBouquetHint: 'Please select a bouquet before adding to order.',
      cartNextStepHint: 'Once it is added, head to cart to choose delivery details and checkout.',
      tryAgain: '다시 시도',
      designingTitle: '꽃다발을 디자인하는 중',
      designingSubtitle: '보통 30~45초 정도 걸립니다',
      today: '오늘',
      yesterday: '어제',
      completed: '완료',
      reviewing: '검토 중',
      creating: '생성 중',
      inProgress: '진행 중',
      chat: '채팅',
      welcomeReferenceHint: '이미지를 첨부해 Fleur에게 당신의 비전을 전달하세요',
      whatsInside: '구성 꽃 목록',
    },
  };

  // ── Init ───────────────────────────────────────────────────────────

  /**
   * @param {object} deps
   * @param {HTMLElement} deps.messagesEl
   * @param {HTMLElement} deps.inputEl
   * @param {HTMLElement} deps.sendBtn
   * @param {string} deps.workerUrl
   * @param {function} deps.onRetry - (text) => void
   * @param {function} deps.onProceed - (selectionRef) => void — user wants to proceed with arrangement
   * @param {function} deps.onRegenerate - () => void — user wants to regenerate
   * @param {function} deps.onRevise - (opts) => void — user selected revise options
   * @param {function} deps.onDecisionProceed - () => Promise<{success:boolean}> — pre-generation proceed CTA
   * @param {function} deps.onDecisionRevise - () => Promise<{success:boolean}> — pre-generation revise CTA
   * @param {function} deps.onOpenWeddingCreative - (prefill) => void — open wedding creative brief
   * @param {function} deps.onOpenWeddingDetails - (prefill) => void — open wedding details form
   * @param {function} deps.onWeddingConceptRegenerate - () => void — enter wedding concept tweak flow
   * @param {function} deps.onWelcomeReferenceCta - () => void — welcome reference upload CTA
   * @param {function} deps.onWelcomeReferenceClear - () => void — clear welcome reference upload
   * @param {function} deps.onWelcomeReferenceRemove - (index) => void — remove image at index from welcome thumbs
   * @param {boolean} deps.addToOrderEnabled - enable Add-to-Order card mode
   */
  function init(deps) {
    messagesEl = deps.messagesEl;
    inputEl = deps.inputEl;
    sendBtn = deps.sendBtn;
    workerUrl = deps.workerUrl;
    analytics = deps.analytics || null;
    onRetry = deps.onRetry || null;
    onProceed = deps.onProceed || null;
    onRegenerate = deps.onRegenerate || null;
    onRevise = deps.onRevise || null;
    onDecisionProceed = deps.onDecisionProceed || null;
    onDecisionRevise = deps.onDecisionRevise || null;
    onOpenWeddingCreative = deps.onOpenWeddingCreative || null;
    onOpenWeddingDetails = deps.onOpenWeddingDetails || null;
    onWeddingConceptRegenerate = deps.onWeddingConceptRegenerate || null;
    onWelcomeReferenceCta = deps.onWelcomeReferenceCta || null;
    onWelcomeReferenceClear = deps.onWelcomeReferenceClear || null;
    onWelcomeReferenceRemove = deps.onWelcomeReferenceRemove || null;
    addToOrderEnabled = !!deps.addToOrderEnabled || !!window.AIFlorist.addToOrderEnabled;
    uiLanguage = detectLanguage();
    ensureImagePreview();
    if (addToOrderEnabled) {
      refreshGoToCartState();
    }

    // Event delegation for price card buttons
    messagesEl.addEventListener('click', function (e) {
      var goCartBtn = e.target.closest('.afc-go-cart-btn');
      if (goCartBtn && goCartBtn.classList.contains('is-disabled')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      var checkoutLink = e.target.closest('.afc-checkout-btn');
      if (checkoutLink && analytics && analytics.trackCheckoutLinkClicked) {
        var captured = captureAttributionForAnalytics();
        analytics.trackCheckoutLinkClicked({
          checkoutHost: getCheckoutHost(checkoutLink.getAttribute('href')),
          utmSource: captured.utmSource,
          utmMedium: captured.utmMedium,
          utmCampaign: captured.utmCampaign,
          utmTerm: captured.utmTerm,
          utmContent: captured.utmContent,
          gclid: captured.gclid,
          gbraid: captured.gbraid,
          wbraid: captured.wbraid,
          fbclid: captured.fbclid,
          msclkid: captured.msclkid,
          ttclid: captured.ttclid,
          fbc: captured.fbc,
          fbp: captured.fbp,
          ga: captured.ga,
          gl: captured.gl,
        });
      }

      var target = e.target.closest('.afc-proceed-btn');
      if (target && !target.disabled && onProceed) {
        if (target.getAttribute('data-require-selection') === 'true' && !hasValidProceedSelection(target)) {
          showProceedHint(target, t('selectBouquetHint'));
          return;
        }
        clearProceedHint(target);
        var defaultLabel = target.getAttribute('data-default-label') || target.textContent || '';
        target.disabled = true;
        target.textContent = addToOrderEnabled ? t('addingToOrder') : t('preparingOrder');
        var payload = buildSelectionRefFromProceedButton(target) || {};
        payload.cardMessage = readCardMessageForButton(target);
        payload.__setProceedState = function (state) {
          if (!addToOrderEnabled) return;
          if (state === 'retrying') {
            target.textContent = t('retryingAddToOrder');
            return;
          }
          if (state === 'finalizing') {
            target.textContent = t('finalizingAddToOrder');
            return;
          }
          if (state === 'adding') {
            target.textContent = t('addingToOrder');
            return;
          }
        };
        Promise.resolve(onProceed(payload))
          .then(function (result) {
            if (result && result.success) {
              if (addToOrderEnabled) {
                if (typeof result.itemCount === 'number') {
                  setHasActiveCartItems(result.itemCount > 0);
                } else {
                  refreshGoToCartState();
                }
                target.textContent = t('addedToOrder');
                setTimeout(function () {
                  target.disabled = false;
                  target.textContent = defaultLabel || t('addToOrder');
                }, 900);
                return;
              }
              return;
            }
            target.disabled = false;
            target.textContent = defaultLabel;
          })
          .catch(function () {
            target.disabled = false;
            target.textContent = defaultLabel;
          });
        return;
      }

      var regen = e.target.closest('.afc-regenerate-btn');
      if (regen && !regen.disabled && onRegenerate) {
        onRegenerate();
        return;
      }

      var reviseToggle = e.target.closest('.afc-revise-toggle-btn');
      if (reviseToggle) {
        var card = reviseToggle.closest('.afc-checkout-card');
        if (!card) return;
        var panel = card.querySelector('.afc-revise-panel');
        if (!panel) return;
        var isOpen = panel.classList.toggle('afc-revise-panel--open');
        panel.style.display = isOpen ? 'block' : 'none';
        if (isOpen) syncReviseApplyState(panel);
        return;
      }

      var reviseOptionInput = e.target.closest('.afc-revise-opt-regenerate, .afc-revise-opt-details');
      if (reviseOptionInput) {
        var optionPanel = reviseOptionInput.closest('.afc-revise-panel');
        if (optionPanel) syncReviseApplyState(optionPanel);
        return;
      }

      var reviseApply = e.target.closest('.afc-revise-apply-btn');
      if (reviseApply && onRevise) {
        var applyCard = reviseApply.closest('.afc-checkout-card');
        if (!applyCard) return;
        var regenCb = applyCard.querySelector('.afc-revise-opt-regenerate');
        var detailsCb = applyCard.querySelector('.afc-revise-opt-details');
        var regenerateProduct = !!(regenCb && regenCb.checked);
        var reviseOrderDetails = !!(detailsCb && detailsCb.checked);
        onRevise({
          regenerateProduct: regenerateProduct,
          reviseOrderDetails: reviseOrderDetails,
        });
        var applyPanel = applyCard.querySelector('.afc-revise-panel');
        if (applyPanel) {
          applyPanel.classList.remove('afc-revise-panel--open');
          applyPanel.style.display = 'none';
        }
        return;
      }

      var welcomeThumbRemove = e.target.closest('.afc-welcome-reference-thumb-remove');
      if (welcomeThumbRemove && onWelcomeReferenceRemove) {
        var wrap = welcomeThumbRemove.closest('.afc-welcome-reference-thumb-wrap');
        if (wrap) {
          var idx = wrap.getAttribute('data-upload-index');
          if (idx !== null) onWelcomeReferenceRemove(parseInt(idx, 10));
        }
        return;
      }

      var welcomeThumbWrap = e.target.closest('.afc-welcome-reference-thumb-wrap');
      if (welcomeThumbWrap && !e.target.closest('.afc-welcome-reference-thumb-remove')) {
        var thumbImg = welcomeThumbWrap.querySelector('.afc-welcome-reference-thumb');
        if (thumbImg && thumbImg.src) openImagePreview(thumbImg.src, 'Reference image');
        return;
      }

      var welcomeReferenceClearBtn = e.target.closest('.afc-welcome-reference-clear-btn');
      if (welcomeReferenceClearBtn) {
        if (onWelcomeReferenceClear) onWelcomeReferenceClear();
        return;
      }

      var welcomeReferenceBtn = e.target.closest('.afc-welcome-reference-btn');
      if (welcomeReferenceBtn && !welcomeReferenceBtn.disabled) {
        var previewImg = welcomeReferenceBtn.querySelector('.afc-welcome-reference-btn-img');
        if (previewImg && previewImg.src) {
          openImagePreview(previewImg.src, 'Reference image');
          return;
        }
        if (onWelcomeReferenceCta) onWelcomeReferenceCta();
        return;
      }

      var decisionProceedBtn = e.target.closest('.afc-decision-proceed-btn');
      if (decisionProceedBtn && !decisionProceedBtn.disabled && onDecisionProceed) {
        var proceedWrap = decisionProceedBtn.closest('.afc-decision-cta');
        setDecisionCtaDisabled(proceedWrap, true);
        Promise.resolve(onDecisionProceed())
          .then(function (result) {
            if (!result || result.success !== true) {
              setDecisionCtaDisabled(proceedWrap, false);
            }
          })
          .catch(function () {
            setDecisionCtaDisabled(proceedWrap, false);
          });
        return;
      }

      var decisionReviseBtn = e.target.closest('.afc-decision-revise-btn');
      if (decisionReviseBtn && !decisionReviseBtn.disabled && onDecisionRevise) {
        var reviseWrap = decisionReviseBtn.closest('.afc-decision-cta');
        setDecisionCtaDisabled(reviseWrap, true);
        Promise.resolve(onDecisionRevise())
          .then(function (result) {
            if (!result || result.success !== true) {
              setDecisionCtaDisabled(reviseWrap, false);
            }
          })
          .catch(function () {
            setDecisionCtaDisabled(reviseWrap, false);
          });
        return;
      }
    });

    // Mobile: touchstart fires immediately; click is delayed. Intercept touch so one tap opens picker.
    if ('ontouchstart' in window) {
      messagesEl.addEventListener('touchstart', function (e) {
        var welcomeReferenceBtn = e.target.closest('.afc-welcome-reference-btn');
        if (!welcomeReferenceBtn || welcomeReferenceBtn.disabled) return;
        var previewImg = welcomeReferenceBtn.querySelector('.afc-welcome-reference-btn-img');
        if (previewImg && previewImg.src) {
          e.preventDefault();
          openImagePreview(previewImg.src, 'Reference image');
          return;
        }
        e.preventDefault();
        if (onWelcomeReferenceCta) onWelcomeReferenceCta();
      }, { passive: false });
    }
  }

  // ── Message rendering ──────────────────────────────────────────────

  /**
   * Render a message bubble.
   *
   * @param {string} role - 'user' | 'assistant' | 'system'
   * @param {string} content - Text content
   * @param {object} [metadata] - Rich metadata (images, prices, checkout)
   * @param {string} [userImageUrl] - Data URL for user-attached image preview
   * @param {Array}  [referenceImages] - Array of { url, name } for library refs
   */
  function isInternalSystemMessage(role, content) {
    if (role === 'system') return true;
    if (typeof content !== 'string') return false;
    return /^\s*\[system\]/i.test(content);
  }

  function message(role, content, metadata, userImageUrl, referenceImages, userImageUrls, options) {
    if (Array.isArray(userImageUrls)) {
      options = options || {};
    } else {
      options = options || {};
      userImageUrls = userImageUrl ? [userImageUrl] : [];
    }
    // Internal system messages are for Fleur's context only — never show to customer.
    // Defense-in-depth: role-based + case-insensitive prefix with optional leading whitespace.
    if (isInternalSystemMessage(role, content)) return;

    // New arrangement/revision context: hide older checkout cards so only the
    // latest checkout path remains visible in the thread.
    if (role === 'assistant' && metadata && (metadata.imageUrl || metadata.variations || metadata.priceBreakdown)) {
      hideStaleCheckoutCards();
    }

    var wrapper = document.createElement('div');
    wrapper.className = 'afc-msg afc-msg--' + role +
      (metadata && metadata.variations && metadata.variations.length > 0 ? ' afc-msg--variations' : '');
    var messageTs = typeof options.timestamp === 'number' ? options.timestamp : Date.now();
    var messageId = String(options.messageId || buildMessageId(role, content, messageTs, metadata));
    var hasImage = !!(
      (userImageUrls && userImageUrls.length > 0) ||
      (metadata && (
        metadata.imageUrl ||
        (metadata.variations && metadata.variations.length > 0) ||
        (metadata.weddingConcepts && metadata.weddingConcepts.length > 0)
      ))
    );
    var replySnippet = buildReplySnippet(content, metadata, hasImage);
    wrapper.setAttribute('data-msg-id', messageId);
    wrapper.setAttribute('data-role', role);
    wrapper.setAttribute('data-msg-ts', String(messageTs));
    wrapper.setAttribute('data-has-image', hasImage ? 'true' : 'false');
    wrapper.setAttribute('data-reply-snippet', replySnippet);
    if (metadata && (metadata.imageUrl || (metadata.variations && metadata.variations[0]) || (metadata.weddingConcepts && metadata.weddingConcepts[0]))) {
      wrapper.setAttribute(
        'data-reply-image-url',
        metadata.imageUrl ||
          ((metadata.variations && metadata.variations[0]) && metadata.variations[0].imageUrl) ||
          ((metadata.weddingConcepts && metadata.weddingConcepts[0]) && metadata.weddingConcepts[0].imageUrl) ||
          ''
      );
    }

    if (role === 'user' && options.replyTo) {
      wrapper.appendChild(buildReplyReference(options.replyTo));
    }

    // Reference images from flower library (above user bubble)
    if (role === 'user' && referenceImages && referenceImages.length > 0) {
      var refRow = document.createElement('div');
      refRow.className = 'afc-ref-images';
      for (var ri = 0; ri < referenceImages.length; ri++) {
        var refImg = document.createElement('img');
        refImg.src = referenceImages[ri].url;
        refImg.alt = referenceImages[ri].name || 'Reference flower';
        refImg.title = referenceImages[ri].name || '';
        refImg.loading = 'lazy';
        refRow.appendChild(refImg);
      }
      wrapper.appendChild(refRow);
    }

    // User-attached image(s) (above the text bubble)
    if (role === 'user' && userImageUrls && userImageUrls.length > 0) {
      for (var ui = 0; ui < userImageUrls.length; ui++) {
        var userImg = document.createElement('img');
        userImg.src = userImageUrls[ui];
        userImg.alt = 'Attached image';
        userImg.className = 'afc-user-image';
        userImg.tabIndex = 0;
        userImg.setAttribute('role', 'button');
        userImg.setAttribute('aria-label', 'Open full-screen image preview');
        userImg.addEventListener('click', function (event) {
          openImagePreview(event.currentTarget.src, 'Attached image');
        });
        userImg.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openImagePreview(event.currentTarget.src, 'Attached image');
          }
        });
        wrapper.appendChild(userImg);
      }
    }

    // Text bubble — skip for metadata-only assistant messages (e.g. checkout card)
    var hasRichContent = metadata && (
      metadata.checkoutUrl ||
      metadata.imageUrl ||
      (metadata.variations && metadata.variations.length > 0) ||
      metadata.priceBreakdown ||
      (metadata.weddingConcepts && metadata.weddingConcepts.length > 0) ||
      (metadata.weddingHandoffCtas && metadata.weddingHandoffCtas.length > 0) ||
      metadata.showWeddingCreativeForm ||
      metadata.showWeddingDetailsForm
    );
    var skipBubble = role === 'assistant' && !content && hasRichContent;

    if (!skipBubble) {
      var bubble = document.createElement('div');
      bubble.className = 'afc-bubble afc-bubble--' + role;

      if (role === 'user') {
        if (content) {
          bubble.textContent = content;
        } else {
          bubble.textContent = t('imageAttached');
          bubble.style.fontStyle = 'italic';
          bubble.style.opacity = '0.7';
        }
      } else {
        bubble.innerHTML = formatAssistantText(content);
      }
      if (role === 'assistant' && options && options.welcomeCta) {
        bubble.appendChild(buildWelcomeReferenceButton());
      }
      if (shouldShowPreGenerationDecisionCta(role, content, metadata, options)) {
        bubble.appendChild(buildPreGenerationDecisionCta());
      }
      wrapper.appendChild(bubble);
    }

    // Image card(s)
    if (metadata && metadata.variations && metadata.variations.length > 0) {
      wrapper.appendChild(buildVariationImageRow(metadata, wrapper));
    } else if (metadata && metadata.imageUrl) {
      wrapper.appendChild(buildImageCard(metadata.imageUrl));
    }

    // Checkout button
    if (metadata && metadata.checkoutUrl && window.AIFlorist.checkoutMode !== 'drawer') {
      wrapper.appendChild(buildCheckoutCard(metadata.checkoutUrl));
    }

    // Price card
    if (metadata && metadata.priceBreakdown) {
      var selectionRef = (metadata.variations && metadata.variations.length > 1)
        ? null
        : buildSelectionRefFromMetadata(metadata);
      var requireSelection = !!(metadata.variations && metadata.variations.length > 1);
      wrapper.appendChild(buildPriceCard(metadata, selectionRef, { requireSelection: requireSelection }));
    }

    if (metadata && metadata.showWeddingCreativeForm) {
      wrapper.appendChild(buildWeddingLaunchCard(metadata.weddingProjectPrefill || null));
    }

    if (metadata && metadata.weddingConcepts && metadata.weddingConcepts.length > 0) {
      wrapper.appendChild(
        buildWeddingConceptGallery(
          metadata.weddingConcepts,
          metadata.weddingConceptActions || [],
          metadata.weddingProjectPrefill || null
        )
      );
    }

    if (metadata && metadata.weddingHandoffCtas && metadata.weddingHandoffCtas.length > 0) {
      wrapper.appendChild(buildWeddingHandoffCard(metadata.weddingHandoffCtas));
    }

    // Flair WhatsApp redirect pill
    if (role === 'assistant' && content && /\bFlair\b/i.test(content)) {
      wrapper.appendChild(buildFlairRedirectPill());
    }

    if (role === 'user' || role === 'assistant') {
      wrapper.appendChild(buildReplyActionButton());
    }

    messagesEl.appendChild(wrapper);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function buildMessageId(role, content, timestamp, metadata) {
    var base = String(role || 'msg') + ':' + String(timestamp || Date.now()) + ':' + String(content || '');
    if (metadata && (metadata.imageUrl || (metadata.variations && metadata.variations.length > 0))) base += ':img';
    if (metadata && metadata.checkoutUrl) base += ':checkout';
    if (metadata && metadata.priceBreakdown) base += ':price';
    if (metadata && metadata.weddingConcepts && metadata.weddingConcepts.length > 0) base += ':wedding-concepts';
    if (metadata && metadata.weddingHandoffCtas && metadata.weddingHandoffCtas.length > 0) base += ':wedding-handoff';
    if (metadata && metadata.showWeddingCreativeForm) base += ':wedding-creative-form';
    if (metadata && metadata.showWeddingDetailsForm) base += ':wedding-details-form';
    var hash = 0;
    for (var i = 0; i < base.length; i++) {
      hash = ((hash << 5) - hash) + base.charCodeAt(i);
      hash |= 0;
    }
    return 'm_' + Math.abs(hash);
  }

  function buildReplySnippet(content, metadata, hasImage) {
    var raw = String(content || '').trim().replace(/\s+/g, ' ');
    if (raw) return raw.slice(0, 120);
    if (metadata && metadata.weddingConcepts && metadata.weddingConcepts.length > 0) return 'Wedding concepts';
    if (hasImage) return 'Image';
    if (metadata && metadata.priceBreakdown) return 'Price details';
    if (metadata && metadata.checkoutUrl) return 'Checkout details';
    if (metadata && metadata.weddingHandoffCtas && metadata.weddingHandoffCtas.length > 0) return 'Wedding handoff';
    if (metadata && (metadata.showWeddingCreativeForm || metadata.showWeddingDetailsForm)) return 'Wedding brief';
    return 'Message';
  }

  function buildReplyReference(replyTo) {
    var ref = document.createElement('div');
    ref.className = 'afc-reply-ref';
    var who = replyTo.role === 'assistant' ? 'Fleur' : 'You';
    ref.innerHTML =
      '<div class="afc-reply-ref-label">Reply to ' + escapeHtml(who) + '</div>' +
      '<div class="afc-reply-ref-snippet">' + escapeHtml(String(replyTo.snippet || '')) + '</div>';
    return ref;
  }

  function buildReplyActionButton() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'afc-reply-btn';
    btn.setAttribute('aria-label', 'Reply to this message');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">' +
        '<path d="M8 7L3 12L8 17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
        '<path d="M4 12H14C17.314 12 20 14.686 20 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
      '</svg>';
    return btn;
  }

  var WELCOME_REFERENCE_BTN_SVG =
    '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>' +
      '<circle cx="8.5" cy="8.5" r="1.5"></circle>' +
      '<polyline points="21 15 16 10 5 21"></polyline>' +
    '</svg>';

  var WELCOME_REFERENCE_ADD_SVG =
    '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="12" y1="5" x2="12" y2="19"></line>' +
      '<line x1="5" y1="12" x2="19" y2="12"></line>' +
    '</svg>';

  function buildWelcomeReferenceButton() {
    var wrap = document.createElement('div');
    wrap.className = 'afc-welcome-reference-cta';
    var btnWrap = document.createElement('div');
    btnWrap.className = 'afc-welcome-reference-btn-wrap';
    var thumbsContainer = document.createElement('div');
    thumbsContainer.className = 'afc-welcome-reference-thumbs';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'afc-welcome-reference-btn';
    btn.setAttribute('aria-label', 'Attach a reference image');
    btn.innerHTML = WELCOME_REFERENCE_BTN_SVG;
    var clearBtn = buildWelcomeReferenceClearButton();
    clearBtn.style.display = 'none';
    btnWrap.appendChild(thumbsContainer);
    btnWrap.appendChild(btn);
    btnWrap.appendChild(clearBtn);
    wrap.appendChild(btnWrap);
    var hint = document.createElement('span');
    hint.className = 'afc-welcome-reference-hint';
    hint.textContent = t('welcomeReferenceHint');
    wrap.appendChild(hint);
    return wrap;
  }

  function shouldShowPreGenerationDecisionCta(role, content, metadata, options) {
    if (role !== 'assistant') return false;
    if (options && options.welcomeCta) return false;
    if (!content || typeof content !== 'string') return false;
    if (metadata && (metadata.checkoutUrl || metadata.imageUrl || metadata.priceBreakdown || (metadata.variations && metadata.variations.length > 0))) {
      return false;
    }
    return /(bring (this|it) to life|shall i|would you like me to create|want me to create|generate (it|this|now)|design (it|this)|create (it|this|now)|would you like me to proceed|confirm and i(?:'|’)ll create|proceed with (?:the )?(?:design|generation|image))/i
      .test(content);
  }

  function buildPreGenerationDecisionCta() {
    var wrap = document.createElement('div');
    wrap.className = 'afc-decision-cta';
    wrap.innerHTML =
      '<button type="button" class="afc-decision-btn afc-decision-btn--secondary afc-decision-revise-btn">' +
        escapeHtml(t('makeChanges')) +
      '</button>' +
      '<button type="button" class="afc-decision-btn afc-decision-btn--primary afc-decision-proceed-btn">' +
        escapeHtml(t('proceedWithDesign')) +
      '</button>';
    return wrap;
  }

  function setDecisionCtaDisabled(wrap, disabled) {
    if (!wrap) return;
    var buttons = wrap.querySelectorAll('button');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].disabled = !!disabled;
    }
  }

  function buildWelcomeReferenceClearButton() {
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'afc-welcome-reference-clear-btn';
    clearBtn.setAttribute('aria-label', 'Remove image');
    clearBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
        '<line x1="18" y1="6" x2="6" y2="18"></line>' +
        '<line x1="6" y1="6" x2="18" y2="18"></line>' +
      '</svg>';
    return clearBtn;
  }

  function escapeAttr(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function renderWelcomeReferenceThumbs(pending) {
    var thumbsEl = messagesEl ? messagesEl.querySelector('.afc-welcome-reference-thumbs') : null;
    var btn = messagesEl ? messagesEl.querySelector('.afc-welcome-reference-btn') : null;
    if (!thumbsEl || !btn) return;

    if (!pending || pending.length === 0) {
      thumbsEl.innerHTML = '';
      thumbsEl.classList.remove('afc-welcome-reference-thumbs--has');
      btn.innerHTML = WELCOME_REFERENCE_BTN_SVG;
      btn.setAttribute('aria-label', 'Attach a reference image');
      var clearBtn = messagesEl.querySelector('.afc-welcome-reference-clear-btn');
      if (clearBtn) clearBtn.style.display = 'none';
      return;
    }

    var html = '';
    for (var i = 0; i < pending.length; i++) {
      var item = pending[i];
      html +=
        '<div class="afc-welcome-reference-thumb-wrap" data-upload-index="' + i + '">' +
          '<img class="afc-welcome-reference-thumb" alt="Attached image ' + (i + 1) + '" src="' + escapeAttr(item.dataUrl) + '" />' +
          '<button class="afc-welcome-reference-thumb-remove" aria-label="Remove image" type="button">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
              '<line x1="18" y1="6" x2="6" y2="18"></line>' +
              '<line x1="6" y1="6" x2="18" y2="18"></line>' +
            '</svg>' +
          '</button>' +
        '</div>';
    }
    thumbsEl.innerHTML = html;
    thumbsEl.classList.add('afc-welcome-reference-thumbs--has');
    btn.innerHTML = WELCOME_REFERENCE_ADD_SVG;
    btn.setAttribute('aria-label', 'Add more reference images');
    var clearBtn = messagesEl.querySelector('.afc-welcome-reference-clear-btn');
    if (clearBtn) clearBtn.style.display = 'none';
  }

  function syncWelcomeReferenceButton(pending) {
    renderWelcomeReferenceThumbs(pending || []);
  }

  function buildImageCard(imageUrl, opts) {
    opts = opts || {};
    var selectable = !!opts.selectable;
    var imgCard = document.createElement('div');
    imgCard.className = 'afc-image-card';

    var skeleton = document.createElement('div');
    skeleton.className = 'afc-img-skeleton';
    imgCard.appendChild(skeleton);

    var img = document.createElement('img');
    img.src = workerUrl + imageUrl;
    img.alt = 'Your custom arrangement';
    img.className = 'afc-arrangement-img';
    img.loading = 'lazy';
    img.tabIndex = 0;
    img.setAttribute('role', 'button');
    img.setAttribute('aria-label', 'Open full-screen image preview');
    img.onload = function () {
      skeleton.remove();
      img.classList.add('afc-img-loaded');
    };
    img.onerror = function () {
      skeleton.innerHTML = '<span class="afc-img-error">' + escapeHtml(t('imageLoadError')) + '</span>';
      img.remove();
    };
    img.addEventListener('click', function () {
      openImagePreview(img.src, img.alt);
    });
    img.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openImagePreview(img.src, img.alt);
      }
    });
    imgCard.appendChild(img);

    var replyBtn = document.createElement('button');
    replyBtn.type = 'button';
    replyBtn.className = 'afc-image-reply-btn';
    replyBtn.setAttribute('aria-label', 'Reply to this image');
    replyBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">' +
        '<path d="M8 7L3 12L8 17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
        '<path d="M4 12H14C17.314 12 20 14.686 20 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
      '</svg>' +
      '<span class="afc-image-reply-label">' + escapeHtml(t('editThisDesign')) + '</span>';
    imgCard.appendChild(replyBtn);

    if (selectable) {
      var selectBtn = document.createElement('button');
      selectBtn.type = 'button';
      selectBtn.className = 'afc-variation-select-btn';
      selectBtn.setAttribute('aria-label', 'Select this arrangement');
      selectBtn.textContent = t('selectVariation');
      imgCard.appendChild(selectBtn);
    }
    return imgCard;
  }

  function buildVariationImageRow(metadata, wrapper) {
    var variations = metadata.variations || [];
    if (variations.length === 0) return document.createElement('div');

    var row = document.createElement('div');
    row.className = 'afc-variation-row';
    row.setAttribute('data-selected-variation', '');

    for (var i = 0; i < variations.length; i++) {
      var v = variations[i];
      var card = buildImageCard(v.imageUrl, { selectable: true, variationIndex: i });
      card.setAttribute('data-variation-index', String(i));
      card.setAttribute('data-variation-image-url', v.imageUrl || '');
      card.setAttribute('data-variation-arrangement-name', v.arrangementName || '');
      card.setAttribute('data-variation-selling-price', String(v.sellingPrice || 0));
      var selectBtn = card.querySelector('.afc-variation-select-btn');
      if (selectBtn) {
        selectBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var clickedCard = e.target.closest('.afc-image-card');
          var parentRow = clickedCard.closest('.afc-variation-row');
          if (!parentRow) return;
          var cards = parentRow.querySelectorAll('.afc-image-card');
          cards.forEach(function (c) { c.classList.remove('afc-selected'); });
          clickedCard.classList.add('afc-selected');
          parentRow.setAttribute('data-selected-variation', clickedCard.getAttribute('data-variation-index') || '');
          var sellingPrice = parseFloat(clickedCard.getAttribute('data-variation-selling-price') || 0);
          var priceAmountEl = wrapper.querySelector('.afc-price-amount');
          if (priceAmountEl) {
            priceAmountEl.textContent = '$' + (Number.isNaN(sellingPrice) ? 0 : sellingPrice).toFixed(2);
          }
          var proceedBtn = wrapper.querySelector('.afc-proceed-btn');
          if (proceedBtn) {
            proceedBtn.setAttribute('data-selection-image-url', clickedCard.getAttribute('data-variation-image-url') || '');
            proceedBtn.setAttribute('data-selection-arrangement-name', clickedCard.getAttribute('data-variation-arrangement-name') || '');
            proceedBtn.setAttribute('data-selection-selling-price', String(sellingPrice));
            clearProceedHintForCard(wrapper.querySelector('.afc-price-card'));
          }
        });
      }
      row.appendChild(card);
    }
    return row;
  }

  function ensureImagePreview() {
    if (imagePreviewEl) return;

    imagePreviewEl = document.createElement('div');
    imagePreviewEl.className = 'afc-image-preview';
    imagePreviewEl.setAttribute('aria-hidden', 'true');
    imagePreviewEl.innerHTML =
      '<button class="afc-image-preview-close" type="button" aria-label="Close image preview">×</button>' +
      '<img class="afc-image-preview-img" alt="Arrangement preview" />';

    imagePreviewImgEl = imagePreviewEl.querySelector('.afc-image-preview-img');

    imagePreviewEl.addEventListener('click', function (event) {
      var isCloseButton = event.target.closest('.afc-image-preview-close');
      if (isCloseButton || event.target === imagePreviewEl) {
        closeImagePreview();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && imagePreviewEl.classList.contains('afc-image-preview--open')) {
        closeImagePreview();
      }
    });

    document.body.appendChild(imagePreviewEl);
  }

  function openImagePreview(src, alt) {
    if (!imagePreviewEl || !imagePreviewImgEl || !src) return;
    imagePreviewImgEl.src = src;
    imagePreviewImgEl.alt = alt || 'Arrangement preview';
    imagePreviewEl.classList.add('afc-image-preview--open');
    imagePreviewEl.setAttribute('aria-hidden', 'false');
    document.body.classList.add('afc-no-scroll');
  }

  function closeImagePreview() {
    if (!imagePreviewEl || !imagePreviewImgEl) return;
    imagePreviewEl.classList.remove('afc-image-preview--open');
    imagePreviewEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('afc-no-scroll');
    imagePreviewImgEl.removeAttribute('src');
  }

  function buildCheckoutCard(checkoutUrl) {
    var card = document.createElement('div');
    card.className = 'afc-checkout-card';
    card.innerHTML =
      '<a href="' + escapeHtml(checkoutUrl) + '" class="afc-checkout-btn" target="_top">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '<polyline points="9 11 12 14 22 4"></polyline>' +
          '<path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>' +
        '</svg>' +
        '<span>' + escapeHtml(t('proceedToCheckout')) + '</span>' +
      '</a>' +
      '<button type="button" class="afc-revise-toggle-btn" style="display:block;margin:12px auto 0;">' + escapeHtml(t('revise')) + '</button>' +
      '<div class="afc-revise-panel" style="display:none;">' +
        '<div class="afc-revise-layout">' +
          '<div class="afc-revise-options">' +
            '<label class="afc-revise-option">' +
              '<input type="checkbox" class="afc-revise-opt-regenerate" /> ' + escapeHtml(t('regenerateProduct')) +
            '</label>' +
            '<label class="afc-revise-option">' +
              '<input type="checkbox" class="afc-revise-opt-details" /> ' + escapeHtml(t('reviseOrderDetails')) +
            '</label>' +
          '</div>' +
          '<button type="button" class="afc-revise-apply-btn" disabled>' + escapeHtml(t('applyRevision')) + '</button>' +
        '</div>' +
      '</div>' +
      '<p class="afc-checkout-note">' + escapeHtml(t('checkoutNote')) + '</p>';
    return card;
  }

  function buildWeddingConceptGallery(concepts, actions, prefill) {
    var card = document.createElement('div');
    card.className = 'afc-wedding-concepts-card';

    var grid = document.createElement('div');
    grid.className = 'afc-wedding-concepts-grid';

    for (var i = 0; i < concepts.length; i++) {
      var concept = concepts[i];
      var conceptEl = document.createElement('div');
      conceptEl.className = 'afc-wedding-concept';

      var imageCard = buildImageCard(concept.imageUrl);
      imageCard.classList.add('afc-wedding-concept-image');
      conceptEl.appendChild(imageCard);

      var body = document.createElement('div');
      body.className = 'afc-wedding-concept-body';

      var title = document.createElement('div');
      title.className = 'afc-wedding-concept-title';
      title.textContent = concept.label || concept.arrangementName || 'Wedding concept';
      body.appendChild(title);

      if (concept.sellingDescription) {
        var copy = document.createElement('p');
        copy.className = 'afc-wedding-concept-copy';
        copy.textContent = concept.sellingDescription;
        body.appendChild(copy);
      }

      if (concept.covers && concept.covers.length > 0) {
        var covers = document.createElement('div');
        covers.className = 'afc-wedding-concept-covers';
        for (var j = 0; j < concept.covers.length; j++) {
          var chip = document.createElement('span');
          chip.className = 'afc-wedding-concept-chip';
          chip.textContent = concept.covers[j];
          covers.appendChild(chip);
        }
        body.appendChild(covers);
      }

      conceptEl.appendChild(body);
      grid.appendChild(conceptEl);
    }

    card.innerHTML =
      '<div class="afc-wedding-concepts-header">' +
        '<span class="afc-wedding-quote-label">Wedding Concepts</span>' +
        '<span class="afc-wedding-concepts-subtitle">Representative visuals generated from your brief</span>' +
      '</div>';
    card.appendChild(grid);
    if (actions && actions.length > 0) {
      card.appendChild(buildWeddingConceptActionBar(actions, prefill));
    }

    return card;
  }

  function buildWeddingLaunchCard(prefill) {
    var card = document.createElement('div');
    card.className = 'afc-wedding-quote-card';
    var title = 'Wedding Visual Brief';
    var copy = 'Open the wedding brief to share the required items, palette, flower direction, and reference files before Fleur generates concepts.';
    var buttonLabel = 'Open wedding brief';

    card.innerHTML =
      '<div class="afc-wedding-quote-header">' +
        '<span class="afc-wedding-quote-label">' + escapeHtml(title) + '</span>' +
      '</div>' +
      '<p class="afc-wedding-brief-copy">' + escapeHtml(copy) + '</p>';

    var actionBtn = document.createElement('button');
    actionBtn.type = 'button';
    actionBtn.className = 'afc-wedding-quote-cta afc-wedding-quote-cta--whatsapp';
    actionBtn.textContent = buttonLabel;
    actionBtn.addEventListener('click', function () {
      if (onOpenWeddingCreative) onOpenWeddingCreative(prefill || null);
    });
    card.appendChild(actionBtn);

    return card;
  }

  function buildWeddingConceptActionBar(actions, prefill) {
    var card = document.createElement('div');
    card.className = 'afc-wedding-concept-actions';
    var hasRegenerate = false;
    var hasProceed = false;
    for (var i = 0; i < actions.length; i++) {
      if (actions[i].type === 'regenerate') hasRegenerate = true;
      if (actions[i].type === 'proceed') hasProceed = true;
    }

    if (hasRegenerate) {
      var regenerateBtn = document.createElement('button');
      regenerateBtn.type = 'button';
      regenerateBtn.className = 'afc-wedding-quote-cta afc-wedding-quote-cta--email';
      regenerateBtn.textContent = 'Regenerate';
      if (onWeddingConceptRegenerate) {
        regenerateBtn.addEventListener('click', function () {
          onWeddingConceptRegenerate();
        });
      }
      card.appendChild(regenerateBtn);
    }

    if (hasProceed) {
      var proceedBtn = document.createElement('button');
      proceedBtn.type = 'button';
      proceedBtn.className = 'afc-wedding-quote-cta afc-wedding-quote-cta--whatsapp';
      proceedBtn.textContent = 'Proceed';
      if (onOpenWeddingDetails) {
        proceedBtn.addEventListener('click', function () {
          onOpenWeddingDetails(prefill || null);
        });
      }
      card.appendChild(proceedBtn);
    }

    return card;
  }

  function buildWeddingHandoffCard(ctas) {
    var card = document.createElement('div');
    card.className = 'afc-wedding-quote-card';

    var ctasHtml = '<div class="afc-wedding-quote-ctas">';
    for (var i = 0; i < (ctas || []).length; i++) {
      var cta = ctas[i];
      ctasHtml +=
        '<a class="afc-wedding-quote-cta afc-wedding-quote-cta--' + escapeAttr(cta.type || 'email') + '" href="' + escapeAttr(cta.href || '#') + '" target="' + (cta.type === 'email' ? '_self' : '_blank') + '" rel="noopener noreferrer">' +
          escapeHtml(cta.label || 'Continue') +
        '</a>';
    }
    ctasHtml += '</div>';

    card.innerHTML =
      '<div class="afc-wedding-quote-header">' +
        '<span class="afc-wedding-quote-label">Wedding Planning Handoff</span>' +
      '</div>' +
      '<p class="afc-wedding-brief-copy">Your creative brief, concept images, and event details are now saved. Continue with our team on your preferred channel.</p>' +
      ctasHtml;

    return card;
  }

  function syncReviseApplyState(panel) {
    if (!panel) return;
    var applyBtn = panel.querySelector('.afc-revise-apply-btn');
    var regenCb = panel.querySelector('.afc-revise-opt-regenerate');
    var detailsCb = panel.querySelector('.afc-revise-opt-details');
    if (!applyBtn) return;
    var hasSelection = !!((regenCb && regenCb.checked) || (detailsCb && detailsCb.checked));
    applyBtn.disabled = !hasSelection;
  }

  function buildPriceCard(metadata, selectionRef, options) {
    options = options || {};
    var pricing = metadata.priceBreakdown;
    var variations = metadata.variations || [];
    var requireSelection = !!options.requireSelection;

    var card = document.createElement('div');
    card.className = 'afc-price-card';

    var arrangeName = metadata.arrangementName || t('arrangementFallbackName');
    var description = metadata.sellingDescription || t('arrangementFallbackDescription');
    var proceedLabel = addToOrderEnabled ? t('addToOrder') : t('proceedToOrder');
    var priceLabel = requireSelection && variations.length > 0
      ? 'From $' + Math.min.apply(null, variations.map(function (v) { return v.sellingPrice || 0; })).toFixed(2)
      : '$' + pricing.sellingPrice.toFixed(2);
    var cardMessageHtml = addToOrderEnabled
      ? (
        '<div class="afc-card-message-field">' +
          '<label class="afc-card-message-label">' +
            escapeHtml(t('cardMessageLabel')) +
          '</label>' +
          '<textarea class="afc-card-message-input" rows="2" ' +
            'placeholder="' + escapeHtml(t('cardMessagePlaceholder')) + '"></textarea>' +
        '</div>'
      )
      : '';
    var cartLinkHtml = addToOrderEnabled
      ? (
        '<a class="afc-price-btn afc-price-btn--secondary afc-go-cart-btn' + (hasActiveCartItems ? '' : ' is-disabled') + '" ' +
          (hasActiveCartItems ? 'href="/cart" target="_top"' : 'aria-disabled="true" tabindex="-1"') +
          ' data-cart-url="/cart">' +
          escapeHtml(t('goToCart')) +
        '</a>'
      )
      : '';
    var nextStepHintHtml = addToOrderEnabled
      ? '<p class="afc-price-next-step">' + escapeHtml(t('cartNextStepHint')) + '</p>'
      : '';

    // Build "What's inside" disclosure from sanitized items (variety + quantity only)
    var recipeItems = (pricing && Array.isArray(pricing.items) && pricing.items.length > 0)
      ? pricing.items
      : [];
    var recipeHtml = '';
    if (recipeItems.length > 0) {
      var rows = recipeItems.map(function (item) {
        return '<li class="afc-price-recipe-item">' +
          escapeHtml(item.quantity + ' × ' + item.variety) +
          '</li>';
      }).join('');
      recipeHtml =
        '<details class="afc-price-recipe">' +
          '<summary class="afc-price-recipe-toggle">' + escapeHtml(t('whatsInside')) + '</summary>' +
          '<ul class="afc-price-recipe-list">' + rows + '</ul>' +
        '</details>';
    }

    card.innerHTML =
      '<div class="afc-price-header">' +
        '<span class="afc-price-label">' + escapeHtml(arrangeName) + '</span>' +
        '<span class="afc-price-amount">' + priceLabel + '</span>' +
      '</div>' +
      '<div class="afc-price-details">' +
        '<span>' + escapeHtml(description) + '</span>' +
      '</div>' +
      recipeHtml +
      nextStepHintHtml +
      cardMessageHtml +
      '<div class="afc-price-actions">' +
        '<button class="afc-price-btn afc-price-btn--secondary afc-regenerate-btn" type="button">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<polyline points="23 4 23 10 17 10"></polyline>' +
            '<path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>' +
          '</svg>' +
          ' ' + escapeHtml(t('regenerate')) +
        '</button>' +
        '<button class="afc-price-btn afc-price-btn--primary afc-proceed-btn" type="button">' +
          escapeHtml(proceedLabel) +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<polyline points="9 18 15 12 9 6"></polyline>' +
          '</svg>' +
        '</button>' +
      '</div>' +
      cartLinkHtml;

    var proceedBtn = card.querySelector('.afc-proceed-btn');
    if (proceedBtn) {
      proceedBtn.setAttribute('data-default-label', proceedLabel);
      if (requireSelection) {
        proceedBtn.setAttribute('data-require-selection', 'true');
      } else if (selectionRef) {
        if (selectionRef.imageUrl) {
          proceedBtn.setAttribute('data-selection-image-url', selectionRef.imageUrl);
        }
        if (selectionRef.arrangementName) {
          proceedBtn.setAttribute('data-selection-arrangement-name', selectionRef.arrangementName);
        }
        if (typeof selectionRef.sellingPrice === 'number') {
          proceedBtn.setAttribute('data-selection-selling-price', String(selectionRef.sellingPrice));
        }
      }
    }

    return card;
  }

  function buildSelectionRefFromMetadata(metadata) {
    if (!metadata) return null;
    if (metadata.variations && metadata.variations.length > 0) {
      var first = metadata.variations[0];
      return {
        imageUrl: first.imageUrl || '',
        arrangementName: first.arrangementName || '',
        sellingPrice: typeof first.sellingPrice === 'number' ? first.sellingPrice : null,
      };
    }
    var pricing = metadata.priceBreakdown || null;
    return {
      imageUrl: metadata.imageUrl || '',
      arrangementName: metadata.arrangementName || '',
      sellingPrice: pricing && typeof pricing.sellingPrice === 'number'
        ? pricing.sellingPrice
        : null,
    };
  }

  function buildSelectionRefFromProceedButton(buttonEl) {
    if (!buttonEl) return null;
    var imageUrl = buttonEl.getAttribute('data-selection-image-url') || '';
    var arrangementName = buttonEl.getAttribute('data-selection-arrangement-name') || '';
    var sellingPriceRaw = buttonEl.getAttribute('data-selection-selling-price');
    var sellingPrice = Number(sellingPriceRaw);
    var ref = {};
    if (imageUrl) ref.imageUrl = imageUrl;
    if (arrangementName) ref.arrangementName = arrangementName;
    if (sellingPriceRaw && !Number.isNaN(sellingPrice)) ref.sellingPrice = sellingPrice;
    return Object.keys(ref).length ? ref : null;
  }

  function hasValidProceedSelection(buttonEl) {
    var ref = buildSelectionRefFromProceedButton(buttonEl);
    return !!(ref && ref.imageUrl);
  }

  function ensureProceedHintEl(card) {
    if (!card) return null;
    var existing = card.querySelector('.afc-price-hint');
    if (existing) return existing;
    var hint = document.createElement('p');
    hint.className = 'afc-price-hint';
    hint.hidden = true;
    var actions = card.querySelector('.afc-price-actions');
    if (actions && actions.parentNode) {
      actions.parentNode.insertBefore(hint, actions);
    } else {
      card.appendChild(hint);
    }
    return hint;
  }

  function showProceedHint(buttonEl, text) {
    if (!buttonEl) return;
    var card = buttonEl.closest('.afc-price-card');
    var hint = ensureProceedHintEl(card);
    if (!hint) return;
    hint.textContent = text || '';
    hint.hidden = false;
  }

  function clearProceedHint(buttonEl) {
    if (!buttonEl) return;
    var card = buttonEl.closest('.afc-price-card');
    clearProceedHintForCard(card);
  }

  function clearProceedHintForCard(card) {
    if (!card) return;
    var hint = card.querySelector('.afc-price-hint');
    if (!hint) return;
    hint.hidden = true;
    hint.textContent = '';
  }

  function formatCurrency(amount) {
    var numeric = Number(amount || 0);
    if (!isFinite(numeric)) numeric = 0;
    return '$' + numeric.toFixed(2);
  }

  function readCardMessageForButton(buttonEl) {
    if (!buttonEl) return '';
    var card = buttonEl.closest('.afc-price-card');
    if (!card) return '';
    var input = card.querySelector('.afc-card-message-input');
    if (!input) return '';
    return String(input.value || '').trim();
  }

  function refreshGoToCartState() {
    fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load cart');
        return res.json();
      })
      .then(function (cart) {
        setHasActiveCartItems(!!(cart && Number(cart.item_count || 0) > 0));
      })
      .catch(function () {
        setHasActiveCartItems(false);
      });
  }

  function setHasActiveCartItems(next) {
    hasActiveCartItems = !!next;
    syncGoToCartButtons();
  }

  function syncGoToCartButtons() {
    if (!messagesEl) return;
    var buttons = messagesEl.querySelectorAll('.afc-go-cart-btn');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var cartUrl = btn.getAttribute('data-cart-url') || '/cart';
      if (hasActiveCartItems) {
        btn.classList.remove('is-disabled');
        btn.setAttribute('href', cartUrl);
        btn.setAttribute('target', '_top');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('tabindex');
      } else {
        btn.classList.add('is-disabled');
        btn.removeAttribute('href');
        btn.removeAttribute('target');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('tabindex', '-1');
      }
    }
  }

  // ── Flair WhatsApp redirect pill ────────────────────────────────────

  var FLAIR_WA_URL = 'https://wa.me/6580373466?text=' + encodeURIComponent('Hi Flair! I need some help please.');

  function buildFlairRedirectPill() {
    var pill = document.createElement('a');
    pill.href = FLAIR_WA_URL;
    pill.target = '_blank';
    pill.rel = 'noopener noreferrer';
    pill.className = 'afc-flair-pill';
    pill.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
        '<path d="M12 2a2 2 0 0 1 2 2v1h1a4 4 0 0 1 4 4v4a4 4 0 0 1-4 4h-1v1.5a1.5 1.5 0 0 1-1.5 1.5h-1A1.5 1.5 0 0 1 10 18.5V17H9a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4h1V4a2 2 0 0 1 2-2zM9 7a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H9z"/>' +
        '<circle cx="9.5" cy="10.5" r="1.5"/><circle cx="14.5" cy="10.5" r="1.5"/>' +
      '</svg>' +
      '<span>Speak to Flair on WhatsApp</span>';
    return pill;
  }

  // ── System error ───────────────────────────────────────────────────

  function systemError(msg, options) {
    var wrapper = document.createElement('div');
    wrapper.className = 'afc-msg afc-msg--system';

    var bubble = document.createElement('div');
    bubble.className = 'afc-bubble afc-bubble--system';

    var text = document.createElement('span');
    text.textContent = msg;
    bubble.appendChild(text);

    // Retry button if applicable
    if (lastFailedMessage && retryCount < MAX_RETRIES) {
      var retryBtn = document.createElement('button');
      retryBtn.className = 'afc-retry-btn';
      retryBtn.textContent = t('tryAgain');
      retryBtn.addEventListener('click', function () {
        wrapper.remove();
        retryCount++;
        if (onRetry) onRetry(lastFailedMessage);
      });
      bubble.appendChild(retryBtn);
    }

    if (options && options.actionLabel && options.onAction) {
      var actionBtn = document.createElement('button');
      actionBtn.className = 'afc-retry-btn';
      actionBtn.textContent = options.actionLabel;
      actionBtn.addEventListener('click', function () {
        wrapper.remove();
        options.onAction();
      });
      bubble.appendChild(actionBtn);
    }

    wrapper.appendChild(bubble);
    messagesEl.appendChild(wrapper);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  /** Track a failed message for retry. */
  function setFailedMessage(text) {
    lastFailedMessage = text;
    retryCount = 0;
  }

  /** Clear failed message tracking (on success). */
  function clearFailedMessage() {
    lastFailedMessage = null;
  }

  // ── Loading indicator ──────────────────────────────────────────────

  /**
   * Legacy loading indicator used by non-streaming sync paths.
   * By default this stays on typing dots. For explicit regenerate-intent flows,
   * allow a delayed shimmer as a UI hint when streaming isn't available.
   */
  function setLoading(loading, options) {
    sendBtn.disabled = loading;
    inputEl.disabled = loading;

    if (designingTimerId) {
      clearTimeout(designingTimerId);
      designingTimerId = null;
    }

    // Remove existing indicators
    removeIndicators();

    if (loading) {
      showTypingDots();
      // Text-only responses arrive in 2-5s (timer cleared before it fires).
      // Image generation takes 25-60s, so the shimmer improves perceived wait.
      var shimmerDelay = (options && options.expectGeneration === true) ? 4000 : 8000;
      designingTimerId = setTimeout(function () {
        removeTypingDots();
        showDesigningCard();
      }, shimmerDelay);
    }

    var isMobile = window.matchMedia('(pointer: coarse)').matches || window.innerWidth <= 768;
    if (!loading && !isMobile) {
      inputEl.focus();
    }
  }

  // ── Streaming-aware indicators ──────────────────────────────────────

  /** Show typing dots indicator. */
  function showTypingDots() {
    removeTypingDots();
    var typing = document.createElement('div');
    typing.className = 'afc-msg afc-msg--assistant afc-typing';
    typing.innerHTML =
      '<div class="afc-bubble afc-bubble--assistant">' +
        '<span class="afc-dot"></span>' +
        '<span class="afc-dot"></span>' +
        '<span class="afc-dot"></span>' +
      '</div>';
    messagesEl.appendChild(typing);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  /** Remove typing dots. */
  function removeTypingDots() {
    var existing = messagesEl.querySelector('.afc-typing');
    if (existing) existing.remove();
  }

  /** Show the "Designing your arrangement" card. */
  function showDesigningCard() {
    removeDesigningCard();
    var designing = document.createElement('div');
    designing.className = 'afc-msg afc-msg--assistant afc-designing';
    designing.innerHTML =
      '<div class="afc-designing-card">' +
        '<div class="afc-designing-shimmer"></div>' +
        '<div class="afc-designing-content">' +
          '<svg class="afc-designing-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M12 22V13"/>' +
            '<path d="M9 19c-2 0-4-1-4-3.5S7 12 9 12"/>' +
            '<path d="M15 19c2 0 4-1 4-3.5S17 12 15 12"/>' +
            '<path d="M12 13c-2.5 0-5-2-5-5s2-5 5-5c3 0 5 2 5 5s-2.5 5-5 5z"/>' +
            '<path d="M12 3c0 2.5 1 5 0 7.5"/>' +
          '</svg>' +
          '<div class="afc-designing-text">' +
            '<span class="afc-designing-title">' + escapeHtml(t('designingTitle')) + '</span>' +
            '<span class="afc-designing-subtitle">' + escapeHtml(t('designingSubtitle')) + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="afc-designing-progress">' +
          '<div class="afc-designing-progress-bar"></div>' +
        '</div>' +
      '</div>';
    messagesEl.appendChild(designing);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    if (window.AIFlorist.game) window.AIFlorist.game.start(messagesEl);
  }

  /** Remove the designing card. */
  function removeDesigningCard() {
    if (window.AIFlorist.game) window.AIFlorist.game.stop();
    var existing = messagesEl.querySelector('.afc-designing');
    if (existing) existing.remove();
  }

  /** Remove all loading indicators (typing + designing). */
  function removeIndicators() {
    removeTypingDots();
    removeDesigningCard();
  }

  /**
   * Lock/unlock the input controls.
   * Used by both streaming and legacy paths.
   */
  function setInputLocked(locked) {
    sendBtn.disabled = locked;
    inputEl.disabled = locked;
    var isMobile = window.matchMedia('(pointer: coarse)').matches || window.innerWidth <= 768;
    if (!locked && !isMobile) inputEl.focus();
  }

  // ── Clear all messages ─────────────────────────────────────────────

  function clearMessages() {
    messagesEl.innerHTML = '';
  }

  /**
   * Hide previously rendered checkout cards.
   * Keeps the UI focused on the latest arrangement revision path.
   */
  function hideStaleCheckoutCards() {
    if (!messagesEl) return;
    var cards = messagesEl.querySelectorAll('.afc-checkout-card');
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.add('afc-checkout-card--stale');
      var link = cards[i].querySelector('.afc-checkout-btn');
      if (link) {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    }
  }

  // ── Utilities ──────────────────────────────────────────────────────

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function formatAssistantText(text) {
    if (typeof window.renderRichText === 'function') {
      return window.renderRichText(text);
    }
    return escapeHtml(text).replace(/\n/g, '<br>');
  }

  function getBadgeClass(state) {
    switch (state) {
      case 'checkout':
      case 'post_purchase':
      case 'verified':
        return 'afc-drawer-item-badge--completed';
      case 'reviewing':
        return 'afc-drawer-item-badge--checkout';
      default:
        return 'afc-drawer-item-badge--active';
    }
  }

  function getBadgeLabel(state) {
    switch (state) {
      case 'checkout':
      case 'post_purchase':
      case 'verified':
        return t('completed');
      case 'reviewing':
        return t('reviewing');
      case 'generating':
        return t('creating');
      case 'gathering_criteria':
        return t('inProgress');
      default:
        return t('chat');
    }
  }

  function formatDate(isoStr) {
    if (!isoStr) return '';
    try {
      var d = new Date(isoStr);
      var now = new Date();
      var diffMs = now.getTime() - d.getTime();
      var diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return t('today');
      if (diffDays === 1) return t('yesterday');
      if (diffDays < 7) return diffDays + 'd ago';

      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function t(key) {
    var dict = DICT[uiLanguage] || DICT.en;
    return dict[key] || DICT.en[key] || key;
  }

  function detectLanguage() {
    var root = document.getElementById('aiflorist-chat-root');
    var raw =
      (root && root.dataset && (root.dataset.language || root.dataset.locale)) ||
      (document.documentElement && document.documentElement.lang) ||
      '';
    var base = String(raw).trim().toLowerCase().replace('_', '-').split('-')[0];
    if (base === 'zh' || base === 'ja' || base === 'ko') return base;
    return 'en';
  }

  function captureAttributionForAnalytics() {
    var api = window.AIFlorist && window.AIFlorist.attribution;
    if (!api || !api.capture) return {};
    try {
      return api.capture() || {};
    } catch (_err) {
      return {};
    }
  }

  function getCheckoutHost(rawUrl) {
    if (!rawUrl) return null;
    try {
      return new URL(rawUrl, window.location.origin).host || null;
    } catch (_err) {
      return null;
    }
  }

  // ── Public API ─────────────────────────────────────────────────────
  window.AIFlorist.render = {
    init: init,
    message: message,
    systemError: systemError,
    renderWelcomeReferenceThumbs: renderWelcomeReferenceThumbs,
    syncWelcomeReferenceButton: syncWelcomeReferenceButton,
    setLoading: setLoading,
    clearMessages: clearMessages,
    hideStaleCheckoutCards: hideStaleCheckoutCards,
    setFailedMessage: setFailedMessage,
    clearFailedMessage: clearFailedMessage,
    escapeHtml: escapeHtml,
    formatDate: formatDate,
    getBadgeClass: getBadgeClass,
    getBadgeLabel: getBadgeLabel,
    // Streaming-specific UI controls
    showTypingDots: showTypingDots,
    removeTypingDots: removeTypingDots,
    showDesigningCard: showDesigningCard,
    removeDesigningCard: removeDesigningCard,
    removeIndicators: removeIndicators,
    setInputLocked: setInputLocked,
    openImagePreview: openImagePreview,
  };
})();
